import { Injectable } from '@angular/core';
import { BaseApiService } from '@ep/shared';
import { catchError, map, of } from 'rxjs';
import {
  CoalitionMerchantActivityRow,
  LocationActivityRange,
  LocationIncomePoint,
} from '@app/areas/dashboard/models/activity-report.model';

@Injectable({
  providedIn: 'root',
})
export class ActivityReportService extends BaseApiService {
  private readonly coalitionsEndpoint = `/v2/Coalitions`;
  private readonly merchantsEndpoint = `/v2/Merchants`;

  /** Per-merchant activity report for a coalition over [from, to). Dates are UTC ISO strings. */
  getCoalitionActivity(coalitionId: number, from: string, to: string) {
    return this.get<unknown>(`${this.coalitionsEndpoint}/${coalitionId}/activity-report`, { from, to }).pipe(
      map((result) => unwrapValues(result).map(mapActivityRow)),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  /** Raw transaction points for a merchant (all locations) over [from, to) (bucketed client-side). */
  getMerchantIncome(merchantId: number, from: string, to: string) {
    return this.get<unknown>(`${this.merchantsEndpoint}/${merchantId}/income-analytics`, { from, to }).pipe(
      map((result) => unwrapValues(result).map(mapIncomePoint)),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  /** Earliest/latest transaction timestamps for a merchant. */
  getMerchantActivityRange(merchantId: number) {
    return this.get<unknown>(`${this.merchantsEndpoint}/${merchantId}/activity-range`).pipe(
      map((result) => mapActivityRange(result)),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }
}

/** Unwrap the .NET `ReferenceHandler.Preserve` collection envelope (`{ $values: [...] }`); pass arrays through. */
function unwrapValues(result: any): any[] {
  if (Array.isArray(result)) {
    return result;
  }
  return result?.$values ?? [];
}

function mapActivityRow(r: any): CoalitionMerchantActivityRow {
  return {
    coalitionId: r.coalitionId ?? r.CoalitionId ?? null,
    merchantId: r.merchantId ?? r.MerchantId,
    merchantName: r.merchantName ?? r.MerchantName ?? '',
    amountTotal: r.amountTotal ?? r.AmountTotal ?? 0,
    transactions: r.transactions ?? r.Transactions ?? 0,
    customers: r.customers ?? r.Customers ?? 0,
    amountTotalMember: r.amountTotalMember ?? r.AmountTotalMember ?? 0,
    amountTotalNonMember: r.amountTotalNonMember ?? r.AmountTotalNonMember ?? 0,
    transactionsMember: r.transactionsMember ?? r.TransactionsMember ?? 0,
    transactionsNonMember: r.transactionsNonMember ?? r.TransactionsNonMember ?? 0,
    members: r.members ?? r.Members ?? 0,
    nonMembers: r.nonMembers ?? r.NonMembers ?? 0,
  };
}

function mapIncomePoint(p: any): LocationIncomePoint {
  return {
    timestamp: toUtcIso(p.timestamp ?? p.Timestamp),
    amountTotal: p.amountTotal ?? p.AmountTotal ?? 0,
  };
}

function mapActivityRange(result: any): LocationActivityRange {
  return {
    earliest: toUtcIso(result?.earliest ?? result?.Earliest ?? null),
    latest: toUtcIso(result?.latest ?? result?.Latest ?? null),
  };
}

function toUtcIso(value: any): any {
  if (typeof value !== 'string' || value.length === 0) {
    return value ?? null;
  }
  return /[zZ]$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
}
